import type { TankWarsGame, Tank, Projectile, Explosion } from "./types.js";
import { CANVAS_W, GROUND_Y, MAX_POWER } from "./types.js";

const TANK_W = 36;
const TANK_H = 16;
const MOVE_SPEED = 120;
const GRAVITY = 280;

export function createGame(): TankWarsGame {
  return {
    tanks: [
      { x: 120, y: GROUND_Y - TANK_H, health: 100, angle: 30, power: 0, width: TANK_W, height: TANK_H },
      { x: CANVAS_W - 120, y: GROUND_Y - TANK_H, health: 100, angle: 150, power: 0, width: TANK_W, height: TANK_H },
    ],
    turn: 0,
    projectile: null,
    explosions: [],
    inFlight: false,
    winner: null,
    wind: (Math.random() - 0.5) * 60,
    message: "Player 1's turn — move mouse to aim, hold to charge, release to fire",
  };
}

export function moveTank(game: TankWarsGame, dir: -1 | 0 | 1): TankWarsGame {
  if (game.inFlight || game.winner !== null) return game;
  const tanks = game.tanks.map((t) => ({ ...t }));
  const t = tanks[game.turn];
  t.x = Math.max(40, Math.min(CANVAS_W - 40, t.x + dir * MOVE_SPEED * 0.016));
  return { ...game, tanks: tanks as [Tank, Tank] };
}

export function setAngle(game: TankWarsGame, delta: number): TankWarsGame {
  if (game.inFlight || game.winner !== null) return game;
  const tanks = game.tanks.map((t) => ({ ...t }));
  tanks[game.turn].angle = Math.max(5, Math.min(175, tanks[game.turn].angle + delta));
  return { ...game, tanks: tanks as [Tank, Tank] };
}

export function setPower(game: TankWarsGame, power: number): TankWarsGame {
  if (game.inFlight || game.winner !== null) return game;
  const tanks = game.tanks.map((t) => ({ ...t }));
  tanks[game.turn].power = Math.max(5, Math.min(MAX_POWER, power));
  return { ...game, tanks: tanks as [Tank, Tank] };
}

export function fire(game: TankWarsGame): TankWarsGame {
  if (game.inFlight || game.winner !== null) return game;
  const tank = game.tanks[game.turn];
  const angleRad = (tank.angle * Math.PI) / 180;
  const speed = tank.power * 4 + 80;
  const dir = game.turn === 0 ? 1 : -1;
  const barrelEndX = tank.x + Math.cos(angleRad) * dir * 20;
  const barrelEndY = (tank.y + 4) - Math.sin(angleRad) * 20;
  const projectile: Projectile = {
    x: barrelEndX, y: barrelEndY,
    vx: Math.cos(angleRad) * speed * dir,
    vy: -Math.sin(angleRad) * speed,
    trail: [{ x: barrelEndX, y: barrelEndY }],
  };
  return {
    ...game,
    projectile,
    explosions: [...game.explosions, { x: barrelEndX, y: barrelEndY, timer: 8, maxTimer: 8 }],
    inFlight: true,
  };
}

function tickExplosions(explosions: Explosion[]): Explosion[] {
  const next: Explosion[] = [];
  for (const e of explosions) {
    if (e.timer > 1) next.push({ ...e, timer: e.timer - 1 });
  }
  return next;
}

export function tickProjectile(game: TankWarsGame, dt: number): TankWarsGame {
  const explosions = tickExplosions(game.explosions);
  if (!game.inFlight || !game.projectile) return { ...game, explosions };

  const dtSec = Math.min(dt / 1000, 0.05);
  const p = { ...game.projectile, trail: [...game.projectile.trail, { x: game.projectile.x, y: game.projectile.y }] };

  p.x += p.vx * dtSec;
  p.y += p.vy * dtSec;
  p.vy += GRAVITY * dtSec;
  p.vx += game.wind * dtSec * 0.3;

  if (p.trail.length > 30) p.trail.shift();

  if (p.y >= GROUND_Y - 4 || p.x < -20 || p.x > CANVAS_W + 20) {
    const hitExplosions = p.y >= GROUND_Y - 4
      ? [...explosions, { x: p.x, y: GROUND_Y - 4, timer: 30, maxTimer: 30 }]
      : explosions;
    return { ...game, projectile: null, explosions: hitExplosions, inFlight: false };
  }

  for (let i = 0; i < 2; i++) {
    const tank = game.tanks[i];
    const dx = p.x - tank.x;
    const dy = p.y - (tank.y + tank.height / 2);
    if (Math.abs(dx) < 22 && Math.abs(dy) < 18) {
      const tanks = game.tanks.map((t) => ({ ...t }));
      tanks[i].health -= 25 + Math.floor(Math.random() * 20);
      const nextTurn: 0 | 1 = game.turn === 0 ? 1 : 0;
      const winner = tanks[i].health <= 0 ? game.turn : null;
      return {
        ...game,
        tanks: tanks as [Tank, Tank],
        projectile: null,
        explosions: [...explosions, { x: p.x, y: p.y, timer: 40, maxTimer: 40 }],
        inFlight: false,
        turn: winner !== null ? game.turn : nextTurn,
        winner,
        message: winner !== null
          ? `Player ${winner + 1} wins!`
          : `Player ${nextTurn + 1}'s turn`,
      };
    }
  }

  return { ...game, projectile: p, explosions };
}
